import React, { useState, useEffect } from 'react';
import { Mail, Instagram, Facebook, MapPin, Phone, Send, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = '/api';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = 'Contact - Salt & Stone';
  }, []);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success('Message sent successfully');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-24" data-testid="contact-page">
      {/* Hero */}
      <section className="py-16 md:py-24 px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <span className="caption block mb-6 animate-fade-in opacity-0" style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }}>
            Get in Touch
          </span>
          <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl text-[#F2F0E9] mb-8 animate-fade-in opacity-0" style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}>
            Contact
          </h1>
          <div className="w-20 h-px bg-[#D4AF37] mx-auto mb-8 animate-fade-in opacity-0" style={{ animationDelay: '0.6s', animationFillMode: 'forwards' }}></div>
          <p className="text-[#F2F0E9]/70 max-w-xl mx-auto leading-relaxed animate-fade-in opacity-0" style={{ animationDelay: '0.8s', animationFillMode: 'forwards' }}>
            We'd love to hear from you. Whether you're a distributor, hospitality partner, or simply curious about Salt & Stone.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-12 md:py-24 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <span className="caption block mb-4">Reach Out</span>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#F2F0E9] mb-8">
                Let's Connect
              </h2>
              <div className="w-12 h-px bg-[#D4AF37] mb-10"></div>

              <div className="space-y-8">
                {/* Email */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 border border-[#D4AF37]/30 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h3 className="text-[#F2F0E9] font-medium mb-1">Email</h3>
                    <a 
                      href="mailto:office@saltandstonegin.com" 
                      className="text-[#F2F0E9]/60 hover:text-[#D4AF37] transition-colors"
                    >
                      office@saltandstonegin.com
                    </a>
                  </div>
                </div>

                {/* Location */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 border border-[#D4AF37]/30 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h3 className="text-[#F2F0E9] font-medium mb-1">Location</h3>
                    <p className="text-[#F2F0E9]/60">
                      Cyprus
                    </p>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="mt-12">
                <h3 className="text-[10px] tracking-[0.2em] text-[#D4AF37] uppercase mb-6">
                  Follow Us
                </h3>
                <div className="flex gap-4">
                  <a 
                    href="https://instagram.com/saltandstonegin" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="social-icon"
                    data-testid="contact-social-instagram"
                  >
                    <Instagram className="w-5 h-5" />
                  </a>
                  <a 
                    href="https://facebook.com/SaltAndStoneGin" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="social-icon"
                    data-testid="contact-social-facebook"
                  >
                    <Facebook className="w-5 h-5" />
                  </a>
                </div>
              </div>

              {/* Distributor CTA */}
              <div className="mt-12 p-8 glass">
                <h3 className="font-['Playfair_Display'] text-xl text-[#F2F0E9] mb-4">
                  Become a Partner
                </h3>
                <p className="text-[#F2F0E9]/60 text-sm mb-4 leading-relaxed">
                  Interested in distributing Salt & Stone Gin? We're always looking for partners who share our passion for quality.
                </p>
                <p className="text-[#D4AF37] text-sm">
                  Use the form to get in touch about partnership opportunities.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="glass p-8 md:p-12">
                {submitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 mx-auto mb-6 border border-[#D4AF37] rounded-full flex items-center justify-center">
                      <Check className="w-8 h-8 text-[#D4AF37]" />
                    </div>
                    <h3 className="font-['Playfair_Display'] text-2xl text-[#F2F0E9] mb-4">
                      Message Sent
                    </h3>
                    <p className="text-[#F2F0E9]/60 mb-8">
                      Thank you for reaching out. We'll get back to you soon.
                    </p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="btn-secondary"
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="contact-form space-y-6" data-testid="contact-form">
                    <div>
                      <label htmlFor="name">Name</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="input-luxury"
                        placeholder="Your name"
                        data-testid="contact-name"
                      />
                    </div>

                    <div>
                      <label htmlFor="email">Email</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="input-luxury"
                        placeholder="your@email.com"
                        data-testid="contact-email"
                      />
                    </div>

                    <div>
                      <label htmlFor="subject">Subject</label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        className="input-luxury"
                        placeholder="How can we help?"
                        data-testid="contact-subject"
                      />
                    </div>

                    <div>
                      <label htmlFor="message">Message</label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={5}
                        className="input-luxury resize-none"
                        placeholder="Your message..."
                        data-testid="contact-message"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="btn-primary w-full flex items-center justify-center gap-2"
                      data-testid="contact-submit"
                    >
                      {loading ? (
                        <div className="spinner w-5 h-5"></div>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Send Message
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="py-24 md:py-32 px-6 border-t border-white/5">
        <div className="container mx-auto max-w-3xl text-center">
          <div className="w-12 h-px bg-[#D4AF37] mx-auto mb-12"></div>
          <blockquote className="quote">
            "Salt & Stone — where the island meets spirit."
          </blockquote>
          <div className="w-12 h-px bg-[#D4AF37] mx-auto mt-12"></div>
        </div>
      </section>
    </div>
  );
}
